import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, EmailValidator, FieldController, PanelHeader, LettersValidator, RequiredValidator, TextField } from '@keplerco/core';
import { FreemiumUserBulkUploadRow } from '../../../../models/bulk-uploads/bulk-upload-dto';
import { IBulkUploadEditFocusPanelProps } from './freemium-bulk-upload.models';

enum FieldName {
  FirstName = 'FirstName',
  LastName = 'LastName',
  Email = 'Email',
  JobTitle = 'JobTitle',
  Department = 'Department',
  Team = 'Team',
  Country = 'Country',
  Languages = 'Languages',
}

type FormValues = Omit<FreemiumUserBulkUploadRow, 'rowNumber' | 'isValid' | 'validationMessages'>;

export default function BulkUploadEditFocusPanel(props: IBulkUploadEditFocusPanelProps) {
  const defaultValues = {
    firstName: props.selectedRow?.firstName ?? '',
    lastName: props.selectedRow?.lastName ?? '',
    email: props.selectedRow?.email ?? '',
    jobTitle: props.selectedRow?.jobTitle ?? ''
  };

  const { control, setValue, trigger } = useForm<any>({
    defaultValues,
    mode: 'onChange',
  });

  const [formValues, setFormValues] = useState<FormValues>(defaultValues);

  useEffect(() => {
    if (props.selectedRow) {
      setValue('firstName', props.selectedRow.firstName);
      setValue('lastName', props.selectedRow.lastName);
      setValue('email', props.selectedRow.email);
      setValue('jobTitle', props.selectedRow.jobTitle);
      trigger([FieldName.FirstName, FieldName.LastName, FieldName.Email, FieldName.JobTitle, FieldName.Department, FieldName.Team, FieldName.Country, FieldName.Languages]);
    }
  }, [props.selectedRow]);

  function handleSubmit() {
    if (!!formValues && !!props.selectedRow) {
      const row: FreemiumUserBulkUploadRow = {
        ...formValues,
        rowNumber: props.selectedRow.rowNumber,
        isValid: props.selectedRow.isValid,
        validationMessages: props.selectedRow.validationMessages,
      };
      props.onSubmit(row);
    }
  }

  function getFieldError(fieldName: string): string | undefined {
    const error = props.selectedRow?.validationMessages?.find(err => err.fieldName === fieldName);
    return error ? error.message : undefined;
  }

  function clearApiError(fieldName: string) {
    if (props.selectedRow) {
      const updatedValidationMessages = props.selectedRow.validationMessages?.filter(err => err.fieldName !== fieldName);

      props.selectedRow.validationMessages = updatedValidationMessages;
      trigger(fieldName);
    }
  }

  return (
    <div className="panelContent">
      <PanelHeader title={`Manage ${props.selectedRow?.firstName} ${props.selectedRow?.lastName}`} divider />

      <form id="editBulkUpload">
        <div className="row">
          <div className="column">
            <FieldController
              control={control}
              rules={new LettersValidator('Enter a first name')}
              name="firstName"
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(FieldName.FirstName);
                return (
                  <TextField
                    {...field}
                    type="text"
                    label="First name"
                    onBlur={() => clearApiError(FieldName.FirstName)}
                    onChange={e => {
                      field.onChange(e);
                      clearApiError(FieldName.FirstName);
                      setFormValues(prev => ({
                        ...prev,
                        firstName: e.target.value
                      }));
                      setTimeout(() => trigger('firstName'), 0);
                    }}
                    responsive
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a first name' }}
                  />
                );
              }}
            />
          </div>

          <div className="column">
            <FieldController
              control={control}
              rules={new LettersValidator('Enter a last name')}
              name="lastName"
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(FieldName.LastName);
                return (
                  <TextField
                    {...field}
                    type="text"
                    onBlur={() => clearApiError(FieldName.LastName)}
                    onChange={e => {
                      field.onChange(e);
                      clearApiError(FieldName.LastName);
                      setFormValues(prev => ({
                        ...prev,
                        lastName: e.target.value
                      }));
                      setTimeout(() => trigger('lastName'), 0);
                    }}
                    label="Last name"
                    responsive
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a last name' }}
                  />
                );
              }}
            />
          </div>
        </div>

        <div className="row">
          <div className="column">
            <FieldController
              control={control}
              rules={new EmailValidator()}
              name="email"
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(FieldName.Email);
                return (
                  <TextField
                    {...field}
                    type="email"
                    onBlur={() => clearApiError(FieldName.Email)}
                    onChange={e => {
                      field.onChange(e);
                      clearApiError(FieldName.Email);
                      setFormValues(prev => ({
                        ...prev,
                        email: e.target.value
                      }));
                      setTimeout(() => trigger('email'), 0);
                    }}
                    label="Email"
                    responsive
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a first name' }}
                  />
                );
              }}
            />
          </div>

          <div className="column">
            <FieldController
              control={control}
              rules={new RequiredValidator('Enter a job title')}
              name="jobTitle"
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(FieldName.JobTitle);
                return (
                  <TextField
                    {...field}
                    type="text"
                    label="Job title"
                    onBlur={() => clearApiError(FieldName.JobTitle)}
                    onChange={e => {
                      field.onChange(e);
                      clearApiError(FieldName.JobTitle);
                      setFormValues(prev => ({
                        ...prev,
                        jobTitle: e.target.value
                      }));
                      setTimeout(() => trigger('jobTitle'), 0);
                    }}
                    responsive
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a job title' }}
                  />
                );
              }}
            />
          </div>
        </div>
      </form>

      <div className="panelFooter" style={{ display: 'flex', justifyContent: 'end' }}>
        <Button type="button" filled onClick={handleSubmit}>
          Update
        </Button>
      </div>
    </div>
  );
}
